import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sun, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This reset link is missing its token. Request a new one."); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Reset failed — the link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAF9]" data-testid="reset-password-page">
      <Seo title="Reset password" description="Set a new HomeSchool Hub password." path="/reset-password" noindex />

      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1770003354577-82b2a2d09d19" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/70 via-orange-400/40 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-white text-amber-500"><Sun className="w-5 h-5" /></span>
            HomeSchool Hub
          </Link>
          <div className="font-display text-3xl font-semibold leading-tight max-w-md">
            One quick step and you&apos;re back to your students.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-semibold tracking-tight">Set a new password</h1>
          <p className="mt-2 text-stone-500 text-sm">Pick something you&apos;ll remember — 8 characters or more.</p>

          {done ? (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-100 p-5" data-testid="reset-done">
              <div className="flex items-center gap-2 font-semibold text-emerald-800">
                <CheckCircle2 className="w-4 h-4" /> Password updated
              </div>
              <p className="mt-2 text-sm text-emerald-800/90">Taking you to the login page…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-2 rounded-xl h-12"
                  autoFocus
                  data-testid="reset-pw-input"
                />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm password</Label>
                <Input
                  id="pw2"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-2 rounded-xl h-12"
                  data-testid="reset-pw2-input"
                />
              </div>
              {err && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reset-error">{err}</div>
              )}
              <div className="flex items-start gap-2 rounded-xl bg-stone-50 border border-stone-200 px-3 py-2.5 text-xs text-stone-600">
                <ShieldCheck className="w-4 h-4 text-emerald-500 flex-none mt-0.5" />
                <span>Use a password you don't reuse elsewhere. You'll be able to sign in with it right away.</span>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base" data-testid="reset-submit">
                {loading ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" /> Updating…</>) : "Save new password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
